/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { RoutingMethodSelectQuery } from '@jira/servicedesk__workforce-optimization/src/__generated__/RoutingMethodSelectQuery.graphql.ts';
import routingMethodSelectQueryParameters from '@jira/servicedesk__workforce-optimization/src/__generated__/RoutingMethodSelectQuery$parameters.ts';
import servicesAndSkillsRowLiveRoutingTableQueryParameters, {
	type ServicesAndSkillsRowLiveRoutingTableQuery,
} from '@jira/servicedesk__workforce-optimization-routing/src/__generated__/ServicesAndSkillsRowLiveRoutingTableQuery.graphql.ts';
import servicesAndSkillsSetupActionPreloadedQueryParameters, {
	type ServicesAndSkillsSetupActionPreloadedQuery,
} from '@jira/servicedesk__workforce-optimization-routing/src/__generated__/ServicesAndSkillsSetupActionPreloadedQuery.graphql.ts';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { WorkforceOptimizationSettingsPageQuery as WorkforceOptimizationSettingsPageRelayQuery } from './__generated__/WorkforceOptimizationSettingsPageQuery.graphql';
import workforceOptimizationSettingsPageQueryParameters from './__generated__/WorkforceOptimizationSettingsPageQuery$parameters.ts';

type WorkforceOptimizationSettingsEntryPointComponent = EntryPointComponent<
	{
		routingMethodSelectQuery: RoutingMethodSelectQuery;
		teamCountsQuery: WorkforceOptimizationSettingsPageRelayQuery;
		servicesAndSkillsQuery?: ServicesAndSkillsRowLiveRoutingTableQuery;
		servicesAndSkillsSetupActionQuery?: ServicesAndSkillsSetupActionPreloadedQuery;
	},
	{},
	{},
	{
		cloudId: string;
		projectKey: string;
	}
>;

export const workforceOptimizationSettingsEntryPoint: EntryPoint<
	WorkforceOptimizationSettingsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<WorkforceOptimizationSettingsEntryPointComponent>(
		/* webpackChunkName: "async-project-settings-service-desk-workforce-optimization", jiraSpaEntry: "async-project-settings-service-desk-workforce-optimization" */ './WorkforceOptimizationSettingsPage.tsx',
	),
	getPreloadProps: ({
		context: { match },
		tenantContext: { cloudId, orgId },
	}: EntryPointRouteParams) => {
		const projectKey: string = match.params.projectKey ?? '';
		const organizationId = `ari:cloud:platform::org/${orgId}`;

		return {
			queries: {
				routingMethodSelectQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: routingMethodSelectQueryParameters,
					variables: { projectKey, cloudId },
				},
				teamCountsQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: workforceOptimizationSettingsPageQueryParameters,
					variables: {
						organizationId,
						siteId: cloudId,
						filter: null,
						sortBy: fg('wfm_onboarding_ui_changes')
							? [{ field: 'DISPLAY_NAME' as const, order: 'ASC' as const }]
							: null,
					},
				},
				...(fg('jsm-routing-release-2')
					? {
							servicesAndSkillsQuery: {
								options: {
									fetchPolicy: 'store-or-network' as const,
								},
								parameters: servicesAndSkillsRowLiveRoutingTableQueryParameters,
								variables: {
									cloudId,
									projectKey,
									isJsmRoutingFixesForGaEnabled: fg('jsm-routing-fixes-for-ga'),
								},
							},
						}
					: {}),
				...(expVal('wfm_onboarding_experiment', 'isEnabled', false)
					? {
							servicesAndSkillsSetupActionQuery: {
								options: {
									fetchPolicy: 'store-or-network' as const,
								},
								parameters: servicesAndSkillsSetupActionPreloadedQueryParameters,
								variables: {
									cloudId,
									projectKey,
									isJsmRoutingFixesForGaEnabled: fg('jsm-routing-fixes-for-ga'),
								},
							},
						}
					: {}),
			},
			extraProps: { cloudId, projectKey },
		};
	},
});
